import { BrainTreeScripts} from "~/types/braintree.type";
import {PaymentMethod} from "../../stores/checkout.store";

export class CreditCardStrategy {
  private hosted_field_instance
  private three_d_secure_instance
  private data_collector
  private customClientErrorHandler: ReturnType<typeof useNuxtApp>['$customClientErrorHandler'];

  constructor() {
    const { $customClientErrorHandler } = useNuxtApp();
    this.customClientErrorHandler = $customClientErrorHandler;
  }

  async  loadBraintreeScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  async initializePayment(client, data_collector) {
    try {
      const checkoutStore = useCheckoutStore();

      const braintree = (window as any).braintree;

      if(!(window as any).braintree || !(window as any).braintree.hostedFields || !(window as any).braintree.threeDSecure){
        await this.loadBraintreeScript(BrainTreeScripts.hostedFields);
        await this.loadBraintreeScript(BrainTreeScripts.threeDSecure);
      }

      this.data_collector = data_collector;
      this.hosted_field_instance = await braintree.hostedFields.create({
        client: client,
        styles: {
          input: {
            "font-size": "16px",
            "font-family": "gilroy, gilroy fallback, sans-serif",
            "font-weight": "lighter",
            color: "#ccc",
          },
          ":focus": {
            color: "black",
          },
          ".valid": {
            color: "#238826",
          },
          ".invalid": {
            color: "red",
          },
        },
        fields: {
          cardholderName: {
            container: "#cardholder-name-input",
            placeholder: useTranslation("cardholderName" , "Naam kaarthouder"),
          },
          number: {
            selector: "#card-number-input",
            placeholder: "4111 1111 1111 1111",
          },
          cvv: {
            selector: "#cvv-input",
            placeholder: "123",
          },
          postalCode: {
            selector: "#postal-code-input",
            placeholder: useTranslation("postCode" , "Postcode"),
          },
          expirationDate: {
            selector: "#card-expiration-input",
            placeholder: "MM/YYYY",
          },
        },
      });
      this.three_d_secure_instance = await braintree.threeDSecure.create({
        client: client,
        version: 2,
      });
      this.hosted_field_instance?.on("validityChange", (event) => {
        checkoutStore.is_card_button = Object.values(event.fields).every(
            (field) => field.isValid
        );
      });

      const button = document.querySelector("#braintree-card-pay");

      button?.addEventListener("click", (e) => {
        e.preventDefault();
        checkoutStore.is_transaction_loading = true;
        checkoutStore.resetError();
        checkoutStore.is_card_button_loading = true;

        try {
          if (this.data_collector && this.data_collector.deviceData) {
            this.processPayment(this.data_collector);
          }
        } catch (err) {
          checkoutStore.is_card_button_loading = false;
          checkoutStore.is_transaction_loading = false;
          
          let errorMeta = {client, data_collector, hostedPaymentError: true}

          this.customClientErrorHandler(
              err,
              'medium',
              'Hosted Field Page Error - trying to initialize payment using initializePayment()',
              errorMeta
          );
        }
      });
    } catch (err) {
      let errorMeta = {client, data_collector, pdpError: true}

      this.customClientErrorHandler(
          err,
          'medium',
          'Client Side Error - trying to fetch product',
          errorMeta
      );
    }
  }

  private async processPayment() {
    try {
      let payload = await this.hosted_field_instance?.tokenize();
      if (!payload) return;
      await this.verify3Ds(payload);
      this.clearFileds();
    } catch (err) {
      this.errorHandler(err);
    }
  }

  private clearFileds() {
    this.hosted_field_instance?.clear("cardholderName");
    this.hosted_field_instance?.clear("number");
    this.hosted_field_instance?.clear("cvv");
    this.hosted_field_instance?.clear("postalCode");
    this.hosted_field_instance?.clear("expirationDate");
  }

  private async verify3Ds(payload) {
    const checkoutStore = useCheckoutStore();
    const trolleyStore = useTrolleyStore();
    const authStore = useAuthStore();
    const user = authStore.user ?? authStore.guest;
    const amount =
        trolleyStore.trolley.order_response?.totals?.inc_vat.raw.toString();

    try {
      if (!this.three_d_secure_instance || !user || !amount) return;

      this.three_d_secure_instance.verifyCard(
          {
            amount: amount,
            nonce: payload.nonce,
            bin: payload.details.bin,
            email: user.email,
            billingAddress: {
              givenName: user.first_name,
              surname: user.last_name,
              streetAddress: checkoutStore.billing_address?.line_1,
              locality: checkoutStore.billing_address?.town,
              region: checkoutStore.billing_address?.country,
            },

            onLookupComplete: function (data: any, next: any) {
              next();
              checkoutStore.is_card_button_loading = false;
            },
          },
          async (err, data) => {

            await checkoutStore.getPaymentToken(true);
            await checkoutStore.initializePaymentMethod(PaymentMethod.GooglePay);

            if (err) {
              console.error(err);
              checkoutStore.is_transaction_loading = false;
              checkoutStore.payment_error.hosted_field_error =
                  "An error occurred during payment processing.";
              return;
            }

            // Handle case where data is undefined
            if (!data) {
              console.error(
                  "3D Secure verification failed: No payload received."
              );
              checkoutStore.is_transaction_loading = false;
              checkoutStore.payment_error.hosted_field_error =
                  "3D Secure verification failed. Please try again.";
              return;
            }

            const { liabilityShifted, liabilityShiftPossible } =
                data.threeDSecureInfo;

            if (liabilityShiftPossible && !liabilityShifted) {
              checkoutStore.payment_error.hosted_field_error =
                  useTranslation('cardPaymentfailed' , 'Kaartbetaling mislukt. Probeer het opnieuw.');
              checkoutStore.is_transaction_loading = false;
              return;
            }

            if (!liabilityShifted) {
              checkoutStore.is_card_button_loading = false;
              checkoutStore.payment_error.hosted_field_error =
              useTranslation('cardPaymentfailed' , 'Kaartbetaling mislukt. Probeer het opnieuw.');
              checkoutStore.is_transaction_loading = false;
              return;
            }
            checkoutStore.is_transaction_loading = true;
            checkoutStore.payment_success = "Card payment successful.";
            checkoutStore.createTransaction(data.nonce, "braintree-card");
          }
      );
    } catch (err) {
      this.errorHandler(err);
    }
  }

  private errorHandler(err) {
    const checkoutStore = useCheckoutStore();
    checkoutStore.is_transaction_loading = false;
    checkoutStore.is_card_button_loading = false;
    checkoutStore.payment_error.hosted_field_error =
    useTranslation('cardPaymentfailed' , 'Kaartbetaling mislukt. Probeer het opnieuw.');

    let errorMeta = {
      hostedPaymentError: true,
      transactionLoader: checkoutStore.is_transaction_loading,
      cardButtonLoader: checkoutStore.is_card_button_loading, 
    }

    this.customClientErrorHandler(
        err,
        'medium',
        'Hosted Field Page Error - trying to initialize payment using initializePayment()',
        errorMeta
    );  
    if (this.hosted_field_instance) {
      this.hosted_field_instance.teardown(async () => {
        await checkoutStore.initializePaymentMethod(PaymentMethod.LocalPay);
      });
    }
  }
}
